import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { LienBouton } from '../components/LienBouton.tsx';
import {
  Carte, Etiquette, IconeAgenda, IconeCheck, IconeCroix, IconeEleves, IconeEmploiDuTemps,
  IconeEvaluation, IconeFichePreparation, IconeHeures, IconeImprimer, IconeJournal,
  IconeProgrammation,
  IconeProgramme, IconeProgressions, IconeSecurite, Message, Squelette,
} from '@journal/ui';
import { creerClientApi } from '../lib/api.ts';
import type { OffrePublique } from '../components/ChoixOffre.tsx';
import { vendDesSieges } from '../components/ChoixOffre.tsx';
import {
  PAR, decrirePaliers, euros, mentionTva, montantPour, type Periodicite,
} from '../lib/prix.ts';
import { PiedDePage } from '../components/PiedDePage.tsx';

/**
 * La page qui présente le produit et ses deux offres, avant tout compte.
 *
 * ── Ce qu'elle doit faire ───────────────────────────────────────────────────
 * Un enseignant qui arrive ici ne sait pas ce qu'est le produit. Une liste de
 * deux cartes portant six mots-clés ne le lui apprend pas : « progressions »
 * ne dit rien à qui n'a pas vu l'écran. Chaque module est donc décrit par ce
 * qu'il évite de faire à la main — c'est la seule chose qui décide quelqu'un.
 *
 * ── Les fonctionnalités viennent de l'API, les descriptions d'ici ───────────
 * `GET /billing/plans` dit ce que CONTIENT chaque offre : c'est la source, et
 * le comparatif s'y règle tout seul. Ajouter une fonctionnalité à une offre
 * dans le catalogue la fait apparaître ici sans toucher à cette page. Les
 * textes, eux, ne peuvent pas venir de la base : ils décrivent des écrans.
 *
 * ── Les prix viennent de Stripe, jamais d'ici ───────────────────────────────
 * Les montants vivent dans Stripe. Les recopier en dur créerait une seconde
 * vérité qui se contredirait au premier changement de tarif. `GET
 * /billing/plans` les lit chez Stripe ; la page les met en mots
 * (`lib/prix.ts`). Un tarif illisible n'est pas remplacé par un chiffre
 * inventé : la carte dit que le prix s'affiche à la souscription.
 *
 * ── Elle n'exige pas de session ─────────────────────────────────────────────
 * Le client API est créé sans jeton : passer par `useSession` obligerait à
 * monter la page dans la partie authentifiée, ce qui est exactement ce qu'on
 * veut éviter.
 */

/** Durée de l'essai, alignée sur `JOURS_ESSAI` du serveur. */
const JOURS_ESSAI = 30;

/**
 * Délai de grâce avant l'effacement d'un espace supprimé.
 *
 * Aligné sur `DELAI_GRACE_JOURS`. Il vaut aujourd'hui trente jours comme
 * l'essai — coïncidence, pas rapport : les confondre ferait mentir cette page
 * le jour où l'un des deux change.
 */
const JOURS_GRACE_SUPPRESSION = 30;

/**
 * Les modules, décrits par le travail qu'ils remplacent.
 *
 * `cle` correspond à la fonctionnalité du catalogue quand il y en a une : c'est
 * ce qui permet au comparatif de se régler sur les offres réelles. Les modules
 * sans clé sont dans toutes les offres — ils font partie du produit, pas de la
 * grille tarifaire.
 */
const MODULES = [
  {
    cle: 'journal',
    nom: 'Cahier journal',
    icone: IconeJournal,
    texte: 'La journée en un tableau : horaire, domaine, objectifs, déroulement, '
      + 'matériel. Une séance se recopie sur toutes les semaines suivantes d’un clic, '
      + 'et la page s’imprime telle qu’elle s’affiche.',
  },
  {
    nom: 'Fiche de préparation',
    icone: IconeFichePreparation,
    texte: 'Vos séquences, leurs séances et le déroulement phase par phase : durée, '
      + 'type, dispositif, objectifs repris du programme et documents joints. '
      + 'Une fiche propre à imprimer, page de garde comprise.',
  },
  {
    cle: 'progressions',
    nom: 'Progressions',
    icone: IconeProgressions,
    texte: 'La période entière d’un seul coup d’œil : les semaines en lignes, vos '
      + 'sous-domaines en colonnes. Ce qui n’a pas été traité le vendredi se reporte '
      + 'à la semaine suivante d’un clic, sans le ressaisir.',
  },
  {
    cle: 'programmation',
    nom: 'Programmation annuelle',
    icone: IconeProgrammation,
    texte: 'Répartissez les objectifs du programme officiel sur les cinq périodes. '
      + 'Ce que vous y posez alimente ensuite vos progressions, puis le cahier journal.',
  },
  {
    cle: 'edt',
    nom: 'Emploi du temps',
    icone: IconeEmploiDuTemps,
    texte: 'Votre semaine type, période par période. C’est elle qui pré-remplit les '
      + 'lignes du cahier journal : vous n’écrivez plus les horaires chaque matin.',
  },
  {
    cle: 'evaluation',
    nom: 'Évaluation par compétences',
    icone: IconeEvaluation,
    texte: 'Une grille par compétence et par élève, quatre niveaux d’acquisition, '
      + 'et la synthèse par domaine qui se calcule seule.',
  },
  {
    nom: 'Agenda',
    icone: IconeAgenda,
    texte: 'Projetez votre agenda en direct devant vos élèves ! Profitez d’un '
      + 'affichage clair et lisible, avec la possibilité de zoomer et d’écrire en '
      + 'mode manuscrit pour faciliter la lecture et le suivi des activités en classe.',
  },
  {
    nom: '108 heures',
    icone: IconeHeures,
    texte: 'Le décompte de vos heures de service : APC, conseils, formations, '
      + 'rendez-vous avec les parents. Les activités qui se répètent se saisissent '
      + 'une fois, et le total annuel se tient à jour tout seul.',
  },
  {
    nom: 'Élèves',
    icone: IconeEleves,
    texte: 'La liste de classe, importable depuis un fichier plutôt que retapée. '
      + 'Elle sert partout ailleurs.',
  },
  {
    nom: 'Programme officiel',
    icone: IconeProgramme,
    texte: 'Le référentiel du ministère est intégré, des trois cycles, et filtré sur '
      + 'le niveau de votre classe. Vous piochez dedans au lieu de recopier un PDF.',
  },
  {
    cle: 'export_pdf',
    nom: 'Impression et export',
    icone: IconeImprimer,
    texte: 'Chaque tableau s’imprime dans sa mise en page : cahier journal, '
      + 'progressions, emploi du temps, grilles. Rien à recomposer pour l’inspection.',
  },
] as const;

/** Libellés du catalogue, pour les lignes du comparatif qui ne sont pas des modules. */
const LIBELLE_FONCTION: Record<string, string> = {
  journal: 'Cahier journal',
  progressions: 'Progressions',
  programmation: 'Programmation annuelle',
  edt: 'Emploi du temps',
  evaluation: 'Évaluation par compétences',
  export_pdf: 'Impression et export',
  equipe: 'Gestion des comptes de l’équipe',
};

/** Ordre de lecture : l'individuelle d'abord, l'École ensuite. */
const RANG: Record<string, number> = { ENSEIGNANT: 0, ECOLE: 1 };

/** Ce que chaque offre promet, en une phrase et trois arguments. */
const ARGUMENTAIRE: Record<string, { pour: string; points: string[] }> = {
  ENSEIGNANT: {
    pour: 'Vous enseignez dans votre classe et vous préparez pour vous.',
    points: [
      'Un compte, tous les modules, sans rien à administrer.',
      'Vos données restent les vôtres : export à tout moment.',
      'Aucune installation : l’application s’ouvre dans le navigateur.',
    ],
  },
  ECOLE: {
    pour: 'Vous dirigez une école et vous équipez votre équipe.',
    points: [
      'Vous achetez le nombre de comptes voulu et vous les créez vous-même.',
      'Chaque enseignant a sa classe et ses données ; personne ne lit celles d’un collègue.',
      'Le compte de direction administre les comptes : il n’ouvre aucun cahier journal.',
    ],
  },
};

/**
 * Le prix d'une offre, en tête de sa carte.
 *
 * Une offre à un compte : « 5 € par mois », puis l'annuel. Une offre à sièges :
 * « à partir de » — le prix au plancher de comptes —, la structure des paliers,
 * et un exemple chiffré, parce qu'un barème se comprend mieux par un cas.
 */
function PrixOffre({ offre }: { offre: OffrePublique }) {
  const tarifs = offre.tarifs ?? {};
  const principale: Periodicite | null = tarifs.MONTH ? 'MONTH' : tarifs.YEAR ? 'YEAR' : null;
  if (!principale) {
    return (
      <p className="text-[13px] text-[var(--color-fg-muted)]">
        Le prix s’affiche au moment de souscrire, avant tout paiement.
      </p>
    );
  }
  const tarif = tarifs[principale]!;
  const aSieges = vendDesSieges(offre);
  const plancher = aSieges ? Math.max(offre.siegesMinimum, 1) : 1;
  const montant = montantPour(tarif, plancher);
  const autre: Periodicite = principale === 'MONTH' ? 'YEAR' : 'MONTH';
  const tarifAutre = tarifs[autre];
  const montantAutre = tarifAutre ? montantPour(tarifAutre, plancher) : null;

  return (
    <div data-testid={`prix-${offre.code}`}>
      {aSieges && (
        <p className="text-[12px] font-medium uppercase tracking-wide text-[var(--color-fg-muted)]">
          À partir de
        </p>
      )}
      <p className="flex flex-wrap items-baseline gap-x-1.5">
        <span className="text-[30px] font-semibold leading-tight tracking-tight">
          {montant !== null ? euros(montant, tarif.devise) : '—'}
        </span>
        <span className="text-[14px] text-[var(--color-fg-muted)]">
          {PAR[principale]}
        </span>
      </p>
      {tarifAutre && montantAutre !== null && (
        <p className="mt-1 text-[13px] text-[var(--color-fg-muted)]">
          {`Ou ${euros(montantAutre, tarifAutre.devise)} ${PAR[autre]}.`}
        </p>
      )}
    </div>
  );
}

/**
 * Réponse à « Combien ça coûte ? », tirée des mêmes tarifs que les cartes.
 * `null` si aucun n'est connu : la question garde alors sa réponse d'attente.
 */
function reponsePrix(offres: OffrePublique[] | null): string | null {
  const phrases = (offres ?? []).flatMap((o) => {
    const t = o.tarifs?.MONTH ?? o.tarifs?.YEAR;
    if (!t) return [];
    const par = PAR[o.tarifs?.MONTH ? 'MONTH' : 'YEAR'];
    const paliers = decrirePaliers(t);
    if (paliers) return [`${o.name} : ${paliers}, ${par}`];
    const m = montantPour(t, 1);
    const an = o.tarifs?.MONTH && o.tarifs?.YEAR ? montantPour(o.tarifs.YEAR, 1) : null;
    return m === null ? [] : [`${o.name} : ${euros(m, t.devise)} ${par}${an !== null ? ` ou ${euros(an, t.devise)} par an` : ''}`];
  });
  return phrases.length === 0 ? null : `${phrases.join('. ')}. ${mentionTva()}.`;
}

/** `-1` en base signifie « sans limite ». */
function limite(valeur: unknown): string {
  const n = Number(valeur);
  if (!Number.isFinite(n)) return '—';
  return n === -1 ? 'Sans limite' : String(n);
}

function Section({
  titre, chapeau, id, children,
}: { titre: string; chapeau?: string; id?: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mt-14 scroll-mt-6">
      <h2 className="text-[21px] font-semibold tracking-tight">{titre}</h2>
      {chapeau && (
        <p className="mt-2 max-w-[46rem] text-[14px] leading-relaxed text-[var(--color-fg-muted)]">
          {chapeau}
        </p>
      )}
      <div className="mt-6">{children}</div>
    </section>
  );
}

/** Bouton d'appel à l'action. Un `<a>`, pas un bouton : il navigue. */
function Appel({ vers, children, principal = false }: {
  vers: string; children: React.ReactNode; principal?: boolean;
}) {
  const classes = [
    'inline-flex h-[42px] items-center rounded-lg px-5 text-[14px] font-medium',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
    principal
      ? 'bg-[var(--color-primary)] text-[var(--color-primary-fg)] shadow-[var(--ombre-carte)] hover:brightness-110'
      : 'border border-[var(--color-border-strong)] bg-[var(--color-surface)] hover:bg-[var(--color-surface-hover)]',
  ].join(' ');
  /*
   * Une ancre de la page (« #formules ») : défilement direct.
   *
   * `<Link to="#formules">` passait par le routeur, qui change l'adresse sans
   * faire défiler : le bouton « Voir les deux formules » ne faisait rien.
   */
  if (vers.startsWith('#')) {
    return (
      <a
        href={vers}
        className={classes}
        onClick={(e) => {
          const cible = document.getElementById(vers.slice(1));
          if (!cible) return;
          e.preventDefault();
          cible.scrollIntoView({ behavior: 'smooth', block: 'start' });
          window.history.replaceState(null, '', vers);
        }}
      >
        {children}
      </a>
    );
  }
  return (
    <Link to={vers} className={classes}>
      {children}
    </Link>
  );
}

export function Offres() {
  const [offres, setOffres] = useState<OffrePublique[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    const api = creerClientApi();
    api
      .requete<OffrePublique[]>('/billing/plans')
      .then((liste) =>
        setOffres([...liste].sort((a, b) => (RANG[a.code] ?? 9) - (RANG[b.code] ?? 9))),
      )
      .catch(() => setErreur('Les offres n’ont pas pu être chargées.'));
  }, []);

  /*
   * Lignes du comparatif : l'union des fonctionnalités de toutes les offres.
   *
   * Partir des offres et non d'une liste écrite ici évite la dérive : une
   * fonctionnalité ajoutée au catalogue apparaît, une retirée disparaît, sans
   * qu'on ait à y penser — et sans qu'un tableau promette ce qui n'est plus
   * vendu.
   */
  const lignesComparatif = [...new Set((offres ?? []).flatMap((o) => o.features))];

  return (
    <div className="min-h-dvh bg-[var(--color-bg)]">
      <header className="border-b border-[var(--color-border)] bg-[var(--color-surface)]">
        <div className="mx-auto flex max-w-[64rem] flex-wrap items-center justify-between gap-3 px-5 py-4">
          <Link
            to="/"
            className="flex items-center gap-2.5 rounded-md focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
          >
            <span
              aria-hidden="true"
              className="grid size-8 shrink-0 place-items-center rounded-lg bg-[var(--color-primary)] text-sm font-bold text-[var(--color-primary-fg)]"
            >
              J
            </span>
            <span className="text-[15px] font-semibold">Journal de Classe</span>
          </Link>
          {/* Un bouton, comme partout ailleurs : un lien souligné perdu dans
              l'en-tête ne se voyait pas comme la porte d'entrée qu'il est. */}
          <LienBouton vers="/" variante="secondary" taille="sm">
            Se connecter
          </LienBouton>
        </div>
      </header>

      <main id="contenu" className="mx-auto max-w-[64rem] px-5 py-12">
        {/* ── Accroche ── */}
        <section>
          <p className="text-[13px] font-semibold uppercase tracking-wide text-[var(--color-primary)]">
            Pour l’école primaire
          </p>
          <h1 className="mt-2 max-w-[36rem] text-[34px] font-semibold leading-tight tracking-tight">
            Préparez une fois. Retrouvez-le partout.
          </h1>
          <p className="mt-4 max-w-[42rem] text-[16px] leading-relaxed text-[var(--color-fg-muted)]">
            Programmation, progressions, cahier journal et évaluation dans un seul
            outil, reliés entre eux : ce que vous répartissez en début d’année
            alimente vos semaines, qui remplissent vos journées. Le programme
            officiel est intégré, vous piochez dedans au lieu de le recopier.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Appel vers="/inscription" principal>
              {`Essayer ${JOURS_ESSAI} jours`}
            </Appel>
            <Appel vers="#formules">Voir les deux formules</Appel>
          </div>
        </section>

        {/* ── Les modules ── */}
        <Section
          titre="Ce que vous y faites"
          chapeau="Une seule application pour gérer votre classe, éliminer les tâches répétitives et simplifier votre quotidien. Plus besoin de multiplier les outils, tout est réuni au même endroit !"
          id="modules"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            {MODULES.map((m) => (
              <div
                key={m.nom}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-[var(--ombre-carte)]"
              >
                <div className="flex items-center gap-2.5">
                  <span
                    aria-hidden="true"
                    className="grid size-9 shrink-0 place-items-center rounded-lg bg-[var(--color-surface-raised)] text-[var(--color-primary)]"
                  >
                    <m.icone taille={18} />
                  </span>
                  <h3 className="text-[15px] font-semibold">{m.nom}</h3>
                </div>
                <p className="mt-3 text-[13.5px] leading-relaxed text-[var(--color-fg-muted)]">
                  {m.texte}
                </p>
              </div>
            ))}
          </div>
        </Section>

        {/* ── Les deux formules ── */}
        <Section
          titre="Deux formules"
          chapeau="La même application dans les deux cas. Ce qui change, c’est le nombre de comptes et qui les administre."
          id="formules"
        >
          {erreur && <Message ton="erreur">{erreur}</Message>}

          {!offres && !erreur && (
            <div className="grid gap-5 sm:grid-cols-2">
              <Squelette className="h-80" />
              <Squelette className="h-80" />
            </div>
          )}

          {offres && (
            <div className="grid gap-5 sm:grid-cols-2">
              {offres.map((offre) => {
                const aSieges = vendDesSieges(offre);
                const argument = ARGUMENTAIRE[offre.code];
                return (
                  <Carte
                    key={offre.code}
                    titre={offre.name}
                    /*
                     * Cinq rangées alignées d'une carte à l'autre : prix,
                     * accroche, points, caractéristiques, bouton. Le prix de
                     * l'offre École tient sur plusieurs lignes : sans cet
                     * alignement, tout ce qui le suit glissait vers le bas, et
                     * « Comptes » ne se lisait plus en face de « Comptes ».
                     */
                    rangees={5}
                    action={
                      aSieges
                        ? <Etiquette ton="info">Plusieurs comptes</Etiquette>
                        : <Etiquette>Un compte</Etiquette>
                    }
                  >
                    <PrixOffre offre={offre} />

                    <p className="mt-4 text-[13.5px] font-medium">{argument?.pour}</p>

                    <ul className="mt-4 flex flex-col gap-2.5">
                      {(argument?.points ?? []).map((p) => (
                        <li key={p} className="flex items-baseline gap-2 text-[13px] leading-relaxed">
                          {/* L'icône est décorative : le libellé porte déjà
                              l'information, et l'annoncer doublerait chaque
                              ligne pour un lecteur d'écran. */}
                          <IconeCheck
                            taille={14}
                            aria-hidden="true"
                            className="shrink-0 translate-y-0.5 text-[var(--color-success)]"
                          />
                          {p}
                        </li>
                      ))}
                    </ul>

                    {/*
                      * Les ÉLÈVES ne figurent plus : ils ne sont plus bornés.
                      * Une école n'en achète pas — l'effectif d'une classe est
                      * subi, pas choisi — et l'annoncer comme une limite
                      * d'offre était à la fois faux et malvenu.
                      */}
                    <dl className="mt-5 grid grid-cols-2 gap-3 border-t border-[var(--color-border)] pt-4 text-[13px]">
                      <div>
                        <dt className="text-[var(--color-fg-muted)]">Comptes</dt>
                        <dd className="font-medium">
                          {aSieges ? 'Au choix' : limite(offre.limits['maxUsers'])}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[var(--color-fg-muted)]">Années scolaires</dt>
                        <dd className="font-medium">{limite(offre.limits['maxSchoolYears'])}</dd>
                      </div>
                      <div>
                        <dt className="text-[var(--color-fg-muted)]">Essai</dt>
                        <dd className="font-medium">{`${JOURS_ESSAI} jours`}</dd>
                      </div>
                    </dl>

                    <p className="mt-4">
                      <Appel vers="/inscription" principal={aSieges}>
                        {aSieges ? 'Créer l’espace de l’école' : 'Créer mon espace'}
                      </Appel>
                    </p>
                  </Carte>
                );
              })}
            </div>
          )}
        </Section>

        {/* ── Comparatif ── */}
        {offres && lignesComparatif.length > 0 && (
          <Section
            titre="Ce que contient chaque formule"
            chapeau="Les modules sont les mêmes. Seule la gestion des comptes distingue l’offre École."
          >
            <div className="overflow-x-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--ombre-carte)]">
              <table className="w-full border-collapse text-sm">
                <caption className="sr-only">
                  Fonctionnalités incluses dans chaque formule
                </caption>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="border-b border-[var(--color-border)] px-4 py-3 text-left text-[12px] font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]"
                    >
                      Fonctionnalité
                    </th>
                    {offres.map((o) => (
                      <th
                        key={o.code}
                        scope="col"
                        className="w-[9rem] border-b border-l border-[var(--color-border)] px-4 py-3 text-center text-[13px] font-semibold"
                      >
                        {o.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lignesComparatif.map((f) => (
                    <tr key={f} className="border-t border-[var(--color-border)]">
                      <th scope="row" className="px-4 py-2.5 text-left text-[13px] font-normal">
                        {LIBELLE_FONCTION[f] ?? f}
                      </th>
                      {offres.map((o) => {
                        const inclus = o.features.includes(f);
                        return (
                          <td
                            key={o.code}
                            className="border-l border-[var(--color-border)] px-4 py-2.5 text-center"
                          >
                            {inclus ? (
                              <IconeCheck
                                taille={16}
                                aria-label="Inclus"
                                className="inline text-[var(--color-success)]"
                              />
                            ) : (
                              <IconeCroix
                                taille={14}
                                aria-label="Non inclus"
                                className="inline text-[var(--color-fg-muted)]"
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* ── Données et confiance ── */}
        <Section
          titre="Vos données, et celles de vos élèves"
          chapeau="Une application scolaire héberge des données de mineurs. Ce n’est pas un détail de configuration."
        >
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              {
                titre: 'Chaque classe est isolée',
                texte: 'Un enseignant ne voit que sa classe. Dans une école, la '
                  + 'direction crée les comptes et répartit les classes : elle n’ouvre '
                  + 'le cahier journal de personne.',
              },
              {
                titre: 'Exportables à tout moment',
                texte: 'Élèves, cahier journal, progressions, évaluations : un fichier '
                  + 'complet, téléchargeable depuis vos réglages. Sans nous le demander.',
              },
              {
                titre: 'Effaçables, avec un délai',
                texte: `La suppression d’un espace s’exécute après ${JOURS_GRACE_SUPPRESSION} jours. `
                  + 'Le temps de se raviser, et pas une minute de plus une fois le délai passé.',
              },
            ].map((c) => (
              <div
                key={c.titre}
                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-[var(--ombre-carte)]"
              >
                <div className="flex items-center gap-2">
                  <IconeSecurite
                    taille={16}
                    aria-hidden="true"
                    className="shrink-0 text-[var(--color-primary)]"
                  />
                  <h3 className="text-[14px] font-semibold">{c.titre}</h3>
                </div>
                <p className="mt-2.5 text-[13px] leading-relaxed text-[var(--color-fg-muted)]">
                  {c.texte}
                </p>
              </div>
            ))}
          </div>
        </Section>

        {/* ── Questions ── */}
        <Section titre="Questions fréquentes">
          <div className="flex flex-col gap-3">
            {[
              {
                q: 'Combien ça coûte ?',
                r: `${reponsePrix(offres) ?? 'Le prix de chaque offre s’affiche au moment de souscrire, avant tout paiement.'} `
                  + `L’essai de ${JOURS_ESSAI} jours est complet et ne demande aucun moyen de `
                  + 'paiement : vous verrez le produit entier avant qu’il soit question d’argent.',
              },
              {
                q: 'Que se passe-t-il à la fin de l’essai ?',
                r: 'Vos données restent en place. Tant qu’aucune offre n’est choisie, l’espace se '
                  + 'limite à la facturation et à l’export de vos données : rien n’est effacé, '
                  + 'et tout revient dès la souscription.',
              },
              {
                q: 'Une école peut-elle ajouter des comptes en cours d’année ?',
                r: 'Oui. Le nombre de comptes se choisit à la souscription et se modifie '
                  + 'ensuite ; un enseignant qui arrive en janvier reçoit son compte le jour même.',
              },
              {
                q: 'Faut-il installer quelque chose ?',
                r: 'Non. L’application s’ouvre dans le navigateur, sur l’ordinateur de la '
                  + 'classe comme sur celui de la maison, avec les mêmes données.',
              },
            ].map((item) => (
              <details
                key={item.q}
                className="group rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-5 py-4 shadow-[var(--ombre-carte)]"
              >
                <summary className="cursor-pointer list-none text-[14px] font-medium marker:content-none focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]">
                  {item.q}
                </summary>
                <p className="mt-2.5 text-[13.5px] leading-relaxed text-[var(--color-fg-muted)]">
                  {item.r}
                </p>
              </details>
            ))}
          </div>
        </Section>

        {/*
          * Pas de bouton « Souscrire » ici.
          *
          * La souscription exige un espace et un propriétaire identifié : y
          * mener directement conduirait à un formulaire de paiement pour un
          * compte qui n'existe pas. L'essai est la seule porte d'entrée, et
          * c'est aussi la plus honnête — on essaie avant de payer.
          */}
        <section className="mt-14 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-7 text-center shadow-[var(--ombre-carte)]">
          <h2 className="text-[19px] font-semibold">Commencer maintenant</h2>
          <p className="mx-auto mt-2 max-w-[38rem] text-[14px] leading-relaxed text-[var(--color-fg-muted)]">
            {`Créez votre espace et travaillez ${JOURS_ESSAI} jours sans engagement. `}
            Le choix de l’offre se fait ensuite, depuis l’écran Abonnement.
          </p>
          <p className="mt-5 flex flex-wrap justify-center gap-3">
            <Appel vers="/inscription" principal>Créer un espace</Appel>
          </p>
        </section>

        <PiedDePage />
      </main>
    </div>
  );
}
